// Parts Finder Horizontal Slider — renders a horizontal product slider that can be
// dropped on any storefront page.
//
// COMPLEX widget: it fetches parts through a hidden communication iframe
// (parts-search-iframe.html) over a postMessage protocol, and initialises a Slick
// carousel over pc-card tiles. Both the iframe/postMessage contract (with per-embed
// sliderId routing) and the Slick init are the widget's own specialised machinery
// and are preserved verbatim here — only the generic base-URL / apiKey / features /
// price-format helpers were swapped for the shared bundle helpers.
//
// MULTI-INSTANCE: a page may carry several sliders. init() iterates EVERY
// [data-parts-slider] container and boots an independent instance (own sliderId,
// own iframe reference, own config) for each — so responses never cross-wire.
//
// Config (read from the TARGET element, not the script tag). Each of the old
// <script data-*> attributes now lives on the container:
//   [data-parts-slider]  — the container to render into
//   data-department / data-brand / data-make / data-model / data-year / data-engine
//   data-category / data-max / data-visible / data-speed / data-dots / data-arrows
//   data-infinite / data-autoplay / data-tablet-columns / data-mobile-columns
//   data-draggable / data-skeleton

import { getAppBaseUrl, getApiKey, inAppPartUrl } from '../shared/config.js';
import { addedToCartToast, addFailedToast } from '../shared/cartToast.js';
import { getTopDocument, getTopBody } from '../shared/dom.js';
import { loadFeatures, readCookie, getSessionRef } from '../shared/identity.js';
import { formatPriceIncl, formatPriceExcl } from '../shared/format.js';
import { vehicleCartData } from '../shared/vehicle.js';

var CONTAINER_SELECTOR = '[data-parts-slider]';

/**
 * Resolve stock qty for a part using warehouse_stock and SelectedBranchName.
 * Returns { qty: number|undefined, branch: string, status: string }
 */
function _resolveStock(part) {
	var branch = '';
	try {
		branch = (window.top || window).localStorage.getItem('SelectedBranchName') || '';
	} catch (e) {
		try { branch = localStorage.getItem('SelectedBranchName') || ''; } catch (e2) { /* ignore */ }
	}

	var ws = part.warehouse_stock;
	if (!ws || typeof ws !== 'object') {
		return { qty: undefined, branch: branch, status: part.stock_status || 'out_of_stock' };
	}

	var qty;
	if (branch) {
		var lowerBranch = branch.toLowerCase();
		for (var key in ws) {
			if (ws.hasOwnProperty(key) && key.toLowerCase() === lowerBranch) {
				qty = ws[key];
				break;
			}
		}
	} else {
		// Sum all warehouses
		qty = 0;
		for (var k in ws) {
			if (ws.hasOwnProperty(k)) qty += (ws[k] || 0);
		}
	}

	var status = qty !== undefined ? (qty > 0 ? 'in_stock' : 'out_of_stock') : (part.stock_status || 'out_of_stock');
	return { qty: qty, branch: branch, status: status };
}

// Build the skeleton-loader markup (a row of `count` shimmering placeholder
// cards shaped like pc-card). Shown while parts load; cleared automatically
// when _renderParts() replaces the slides container's contents.
function _skeletonHtml(count) {
	var n = (typeof count === 'number' && count > 0) ? count : 5;
	var shimmer = 'background:linear-gradient(90deg,#eef1f4 25%,#f6f8fa 37%,#eef1f4 63%);background-size:200% 100%;animation:pfSkelShimmer 1.4s ease infinite;';
	var bar = function (w, h, mt) {
		return '<div style="width:' + w + ';height:' + h + 'px;border-radius:4px;' + (mt ? 'margin-top:2px;' : '') + shimmer + '"></div>';
	};
	var card =
		'<div style="flex:1 1 0;min-width:0;border:1px solid #eef1f4;border-radius:8px;background:#fff;overflow:hidden;display:flex;flex-direction:column;">' +
			'<div style="width:100%;aspect-ratio:1/1;' + shimmer + '"></div>' +
			'<div style="padding:14px;display:flex;flex-direction:column;gap:10px;">' +
				bar('46%', 12) + bar('92%', 14) + bar('64%', 14) + bar('38%', 12, true) + bar('54%', 18, true) +
			'</div>' +
			'<div style="margin-top:auto;padding:14px;display:flex;gap:8px;">' +
				'<div style="flex:1;height:38px;border-radius:6px;' + shimmer + '"></div>' +
				'<div style="width:44px;height:38px;border-radius:6px;' + shimmer + '"></div>' +
			'</div>' +
		'</div>';
	var cards = '';
	for (var i = 0; i < n; i++) cards += card;
	return '<div style="display:flex;gap:16px;width:100%;box-sizing:border-box;' +
		'font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,sans-serif;">' + cards + '</div>';
}

/**
 * Override the catalogue (Solr) prices — which are the STANDARD / B2C list price
 * — with the logged-in customer's role-based price, the same way the results grid
 * and product-price widget do. Without this the slider always showed B2C prices
 * regardless of who's logged in. Identity is the storefront Session cookie +
 * commerce_features JWT (NOT the Bearer key), so this is a raw fetch. Mutates
 * `parts[i].price` in place. Fails open (keeps the catalogue price) and no-ops for
 * anonymous visitors (no session → B2C is correct for them).
 */
function _applyCustomerPrices(parts) {
	try {
		var skus = [];
		for (var i = 0; i < parts.length; i++) { var s = parts[i] && parts[i].sku; if (s) skus.push(s); }
		if (skus.length === 0) return Promise.resolve();
		var sessionRef = getSessionRef();
		var jwt = readCookie('commerce_features');
		if (!sessionRef && !jwt) return Promise.resolve(); // anonymous → catalogue (B2C) price is correct
		var base = getAppBaseUrl().replace(/\/$/, '');
		var headers = { 'Accept': 'application/json' };
		if (sessionRef) headers['X-Session-Reference'] = sessionRef;
		if (jwt) headers['X-Commerce-Features'] = jwt;
		return fetch(base + '/api/prices/customer?skus=' + encodeURIComponent(skus.join(',')), { method: 'GET', headers: headers, cache: 'no-store' })
			.then(function (r) { return r.ok ? r.json() : { prices: [] }; })
			.then(function (data) {
				var bySku = {};
				var list = (data && data.prices) ? data.prices : [];
				for (var i = 0; i < list.length; i++) {
					var p = list[i];
					if (p && p.sku && typeof p.price === 'number' && p.price > 0) bySku[p.sku] = p;
				}
				for (var j = 0; j < parts.length; j++) {
					var part = parts[j];
					var ov = part && part.sku ? bySku[part.sku] : null;
					if (ov) {
						part.price = ov.price; // excl-VAT customer price (same unit as the Solr price)
						if (typeof ov.reducedFrom === 'number' && ov.reducedFrom > ov.price) part.reducedFrom = ov.reducedFrom;
					}
				}
			})
			.catch(function () { /* keep catalogue price on error */ });
	} catch (e) { return Promise.resolve(); }
}

/**
 * POST to storefront /ajax/cart/add-item with JSON { Sku, Qty }.
 * Storefront accepts SKU and converts to ProductId server-side (ConvertSkuService).
 */
function _addToCartWithSku(sku, qty, optButtonEl) {
	if (!sku) return;
	// Credit gate — see storefront-credit-guard.js. Degrades to proceeding
	// when the guard script isn't present.
	if (window.PFCreditGuard && window.PFCreditGuard.guard) {
		window.PFCreditGuard.guard({ sku: sku, qty: qty }).then(function (ok) { if (ok) _addToCartWithSkuNow(sku, qty, optButtonEl); });
		return;
	}
	_addToCartWithSkuNow(sku, qty, optButtonEl);
}
function _addToCartWithSkuNow(sku, qty, optButtonEl) {
	qty = qty || 1;
	var addToCartOrigin = (window.top && window.top.location) ? window.top.location.origin : window.location.origin;
	var addToCartUrl = addToCartOrigin + '/ajax/cart/add-item';
	var showLoading = function(show) {
		if (!optButtonEl) return;
		optButtonEl.style.pointerEvents = show ? 'none' : '';
		optButtonEl.setAttribute('aria-busy', show ? 'true' : 'false');
		if (show) {
			// Icon-only spinner — the cart button is a small square (add_shopping_cart
			// icon); a " Adding..." label overflows and gets clipped (GW feedback).
			// Self-contained CSS spinner (not Font Awesome): the storefront ships
			// Material Symbols, so an <i class="fa"> would render blank. currentColor
			// makes it inherit the button's text colour (white on both buttons).
			optButtonEl._originalAddContent = optButtonEl.innerHTML;
			optButtonEl.innerHTML = '<span style="display:inline-block;width:1em;height:1em;border:2px solid currentColor;border-top-color:transparent;border-radius:50%;animation:parts-slider-spin .8s linear infinite;vertical-align:middle;"></span>';
		} else if (optButtonEl._originalAddContent) {
			optButtonEl.innerHTML = optButtonEl._originalAddContent;
		}
	};
	showLoading(true);
	// GW-407: stamp the active vehicle filter onto the line's `Data` so it lands
	// on the order line item. Empty string when no vehicle is selected.
	var payload = { Sku: sku, Qty: qty };
	var vehData = vehicleCartData();
	if (vehData) payload.Data = vehData;
	var body = JSON.stringify(payload);
	fetch(addToCartUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
		body: body,
		credentials: 'include'
	})
		.then(function(res) { return res.json().then(function(data) { return { ok: res.ok, data: data }; }); })
		.then(function(addResult) {
			showLoading(false);
			var success = addResult && addResult.data && (addResult.data.Success === true || addResult.data.success === true);
			var toastr = window.Utils && typeof window.Utils.Toastr === 'function' ? window.Utils.Toastr : null;
			if (addResult && addResult.ok && success) {
				if (window.Cart && window.Cart.MiniCart && typeof window.Cart.MiniCart.GetMiniCart === 'function') {
					window.Cart.MiniCart.GetMiniCart();
				}
				if (window.Cart && typeof window.Cart.UpdateCartCount === 'function') window.Cart.UpdateCartCount();
				if (toastr) toastr(addedToCartToast(null, 1));
			} else if (addResult && addResult.data) {
				console.warn('PartsSlider: add-item returned', addResult.data);
				var msg = addResult.data.Message || addResult.data.message || 'Could not add to cart. Please try again.';
				var warnings = addResult.data.Warnings || addResult.data.warnings;
				if (warnings && Array.isArray(warnings) && warnings.length) {
					var warningTexts = warnings.map(function(w) { return w.Message || w.message; }).filter(Boolean);
					if (warningTexts.length) msg += ' ' + warningTexts.join(' ');
				}
				if (toastr) toastr(addFailedToast(msg));
				else if (window.alert) window.alert(msg);
			}
		})
		.catch(function(err) {
			showLoading(false);
			console.warn('PartsSlider: add to cart failed', err);
			var toastr = window.Utils && typeof window.Utils.Toastr === 'function' ? window.Utils.Toastr : null;
			if (toastr) toastr(addFailedToast('Could not add to cart. Please try again.'));
			else if (window.alert) window.alert('Could not add to cart. Please try again.');
		});
}

// Each [data-parts-slider] on the page becomes an independent instance. All the
// per-embed state (container, sliderId, config, comm iframe, resolved base URL /
// api key) lives inside this closure so multiple sliders never stomp one another —
// this is the module equivalent of the legacy per-<script> IIFE scope.
function createSlider(container) {
	var _appBaseUrl = getAppBaseUrl();
	var _apiKey = getApiKey();
	var _iframe = null;
	var _container = null;
	var _sliderId = null;
	var _config = {};
	// The first parts response wins; any later duplicate for this slider is ignored
	// (see _handleMessageFromIframe).
	var _responded = false;

	// The built-in skeleton markers, plus whatever the embed's `data-skeleton`
	// attribute (now on the container) specifies. `data-skeleton` accepts a bare
	// class name (data-skeleton="my-skeleton"), a full CSS selector
	// (data-skeleton=".foo #bar"), or a comma-separated list of either.
	function _skeletonSelectors() {
		// When THIS embed sets data-skeleton, target ONLY those selectors — so with
		// several sliders on the page each removes only its own skeleton (give each
		// skeleton block a distinct class + matching data-skeleton). With no
		// data-skeleton, fall back to the built-in markers.
		var custom = [];
		try {
			var raw = _config.skeleton;
			if (raw) {
				raw.split(',').forEach(function (part) {
					var s = part.trim();
					if (!s) return;
					// A bare identifier (letters/digits/-/_) is treated as a class name.
					if (/^[A-Za-z_-][\w-]*$/.test(s)) s = '.' + s;
					custom.push(s);
				});
			}
		} catch (e) { /* ignore */ }
		if (custom.length) return custom.join(', ');
		return '.pf-slider-skeleton, [data-pf-slider-skeleton]';
	}

	// Remove any page-level skeleton placeholder the site rendered before the slider
	// script loaded (so it paints instantly with the server HTML). Mark that block
	// with class "pf-slider-skeleton", attribute [data-pf-slider-skeleton], or point
	// the embed at it via the container's data-skeleton attribute (see
	// _skeletonSelectors). Removed once the real slider has data (or errors out).
	function _removeExternalSkeleton() {
		try {
			var doc = getTopDocument();
			var nodes = doc.querySelectorAll(_skeletonSelectors());
			for (var i = 0; i < nodes.length; i++) {
				var el = nodes[i];
				if (el && el.parentNode) el.parentNode.removeChild(el);
			}
		} catch (e) { /* ignore */ }
	}

	function _renderSliderIframe() {
		// Always work with top-level document for iframe placement
		var topDoc = getTopDocument();
		var topBody = getTopBody();

		// Check if search iframe already exists (prefer this one as it's most likely to be on correct origin)
		var existingIframe = topDoc.getElementById('parts-search-iframe');
		if (existingIframe) {
			_iframe = existingIframe;
			// Store the iframe's origin for postMessage
			try {
				var iframeSrc = existingIframe.src;
				if (iframeSrc) {
					var iframeUrl = new URL(iframeSrc);
					_appBaseUrl = iframeUrl.origin + iframeUrl.pathname.replace(/\/parts-search-iframe\.html.*$/, '/');
					if (!_appBaseUrl.endsWith('/')) {
						_appBaseUrl += '/';
					}
				}
			} catch (e) {
				console.warn('PartsSlider: Could not determine iframe origin, using script origin');
			}
			// If reusing an existing iframe, wait a bit for it to be ready, then request parts
			// The iframe should send IFRAME_READY message, which will trigger _requestParts()
			setTimeout(function() {
				if (_iframe && _iframe.contentWindow) {
					_requestParts();
				}
			}, 100);
			return;
		}

		// Check if embed script iframe already exists. Another slider on the page may
		// already have created the comm iframe below, so share it rather than loading
		// a second copy of the iframe app.
		existingIframe = topDoc.getElementById('parts-finder-comm-iframe') || topDoc.getElementById('parts-slider-iframe');
		if (existingIframe) {
			_iframe = existingIframe;
			existingIframe.setAttribute('data-parts-slider', '');
			// Store the iframe's origin for postMessage
			try {
				var iframeSrc = existingIframe.src;
				if (iframeSrc) {
					var iframeUrl = new URL(iframeSrc);
					_appBaseUrl = iframeUrl.origin + iframeUrl.pathname.replace(/\/parts-search-iframe\.html.*$/, '/');
					if (!_appBaseUrl.endsWith('/')) {
						_appBaseUrl += '/';
					}
				}
			} catch (e) {
				console.warn('PartsSlider: Could not determine iframe origin, using script origin');
			}
			// If reusing an existing iframe, wait a bit for it to be ready, then request parts
			// The iframe should send IFRAME_READY message, which will trigger _requestParts()
			setTimeout(function() {
				if (_iframe && _iframe.contentWindow) {
					_requestParts();
				}
			}, 100);
			return;
		}

		// Create hidden iframe for communication ONLY - not for rendering content
		var iframeSrc = _appBaseUrl + 'parts-search-iframe.html';

		var iframe = topDoc.createElement('iframe');
		iframe.setAttribute('data-parts-slider', '');
		iframe.id = 'parts-slider-iframe';
		iframe.height = '1';
		iframe.width = '1';
		iframe.style.cssText = 'border:0; position:absolute; left:-9999px; top:-9999px; visibility:hidden; width:1px; height:1px;';
		iframe.scrolling = 'no';
		iframe.src = iframeSrc;
		iframe.setAttribute('aria-hidden', 'true');
		iframe.setAttribute('tabindex', '-1');

		// Append to top-level body, not any iframe body
		topBody.appendChild(iframe);
		_iframe = iframe;


		// Set up iframe load handler to request parts when ready
		iframe.onload = function() {
			// The iframe will send IFRAME_READY message, which will trigger _requestParts()
		};
	}

	function _requestParts() {
		if (!_iframe || !_iframe.contentWindow) {
			setTimeout(_requestParts, 500);
			return;
		}

		_sendMessageToIframe({
			type: 'GET_SLIDER_PARTS',
			data: {
				config: _config,
				// Tag the request with THIS embed's slider id so, on a page with
				// multiple sliders (each with its own message listener), each embed
				// only renders the response meant for it (see _handleMessageFromIframe).
				sliderId: _sliderId
			}
		});
	}

	function _renderParts(parts) {
		if (!_container) return;

		var slidesContainer = getTopDocument().getElementById(_sliderId + '-slides');
		if (!slidesContainer) return;

		// Remove any external (page-level) skeleton placeholder now that the real
		// slider has data. See _removeExternalSkeleton for the markers it targets.
		_removeExternalSkeleton();

		// Clear loading. If Slick already runs on this container (a re-render), stop it
		// first: wiping its markup underneath it leaves every slide stacked in a tall
		// column until it restarts.
		var jq = window.jQuery;
		if (jq && jq.fn && jq.fn.slick && jq(slidesContainer).hasClass('slick-initialized')) {
			jq(slidesContainer).slick('unslick');
		}
		slidesContainer.innerHTML = '';

		if (!parts || parts.length === 0) {
			slidesContainer.innerHTML = '<div style="padding: 40px; text-align: center; color: #6c757d;">No products found</div>';
			return;
		}

		// Create slides directly in the slides container
		// Slick will wrap them in slick-list and slick-track automatically when initialized
		// Each slide is a col-xs-6 col-md-3 (or based on config)
		var colMdClass = 'col-md-3'; // fixed like core; Slick sets per-slide width inline from slidesToShow
		parts.forEach(function(part, index) {
			var slide = getTopDocument().createElement('div');
			slide.className = 'col-xs-6 ' + colMdClass + ' slick-slide';
			slide.setAttribute('data-slick-index', index);
			slide.setAttribute('role', 'option');
			slide.setAttribute('aria-describedby', 'slick-slide' + (index + 40));

			// The card (<article class="pc-card">) sits directly inside the slide,
			// mirroring the OE Commerce core product-grid markup.
			var card = _createProductBox(part);
			slide.appendChild(card);
			slidesContainer.appendChild(slide);
		});

		// Initialize Slick slider if available
		var $ = window.jQuery;
		if (typeof $ !== 'undefined' && $ && $.fn && $.fn.slick) {
			setTimeout(function() {
				var $slider = $('#' + _sliderId + '-slides');
				if ($slider.length) {
					// Destroy existing slick instance if any
					if ($slider.hasClass('slick-initialized')) {
						$slider.slick('unslick');
					}

					// Initialize Slick - it will automatically wrap slides in slick-list > slick-track
					// Find the arrow buttons (they're siblings of the slides container, inside product-grid)
					var $productGrid = $('#' + _sliderId);
					var $prevBtn = $productGrid.find('.slider-prev').first();
					var $nextBtn = $productGrid.find('.slider-next').first();


					$slider.slick({
						slidesToShow: parseInt(_config.visible || 3),
						slidesToScroll: parseInt(_config.visible || 3),
						dots: _config.dots !== false,
						arrows: true, // Enable arrows so Slick can manage them
						infinite: _config.infinite !== false,
						speed: parseInt(_config.speed || 400),
						autoplay: _config.autoplay || false,
						autoplaySpeed: _config.autoplaySpeed ? parseInt(_config.autoplaySpeed) : 3000,
						draggable: _config.draggable !== false,
						prevArrow: $prevBtn.length ? $prevBtn : $('<button type="button" class="slick-prev">Previous</button>'),
						nextArrow: $nextBtn.length ? $nextBtn : $('<button type="button" class="slick-next">Next</button>'),
						responsive: [
							{
								breakpoint: 992,
								settings: {
									slidesToShow: parseInt(_config.tabletColumns || 2),
									slidesToScroll: parseInt(_config.tabletColumns || 2)
								}
							},
							{
								breakpoint: 768,
								settings: {
									slidesToShow: parseInt(_config.mobileColumns || 1),
									slidesToScroll: parseInt(_config.mobileColumns || 1)
								}
							}
						]
					});
				}
			}, 200);
		} else {
			console.warn('PartsSlider: Slick slider not available, using basic layout');
			// Apply basic styling for non-slick layout
			slidesContainer.style.cssText = 'display: flex; flex-wrap: nowrap; overflow-x: auto;';
		}
	}

	/**
	 * Resolve storefront product URL via Parts Finder API (uses OrderEazi SDK server-side), then redirect.
	 * Optional: pass a button/link element to show loading state while fetching.
	 */
	function _navigateToPartDetailUrl(partId, optButtonEl) {
		var apiUrl = _appBaseUrl.replace(/\/$/, '') + '/api/parts/' + encodeURIComponent(partId) + '/storefront-url';
		var fallbackUrl = inAppPartUrl(partId);
		var showLoading = function(show) {
			if (!optButtonEl) return;
			optButtonEl.style.pointerEvents = show ? 'none' : '';
			optButtonEl.setAttribute('aria-busy', show ? 'true' : 'false');
			if (show) {
				optButtonEl._originalContent = optButtonEl.innerHTML;
				optButtonEl.innerHTML = '<span style="display:inline-block;width:1em;height:1em;border:2px solid currentColor;border-top-color:transparent;border-radius:50%;animation:parts-slider-spin 0.8s linear infinite;vertical-align:middle;"></span> <span>Loading...</span>';
			} else if (optButtonEl._originalContent) {
				optButtonEl.innerHTML = optButtonEl._originalContent;
			}
		};
		showLoading(true);
		var headers = { 'Accept': 'application/json' };
		if (_apiKey) headers['Authorization'] = 'Bearer ' + _apiKey;
		fetch(apiUrl, { method: 'GET', headers: headers })
			.then(function(r) { return r.json().then(function(data) { return { ok: r.ok, data: data }; }); })
			.then(function(result) {
				var url = (result.data && result.data.url) ? result.data.url.trim() : '';
				if (url) {
					window.location.href = url;
				} else {
					showLoading(false);
					window.location.href = fallbackUrl;
				}
			})
			.catch(function(err) {
				console.warn('PartsSlider: storefront URL request failed', err);
				showLoading(false);
				window.location.href = fallbackUrl;
			});
	}

	function _createProductBox(part) {
		var topDoc = getTopDocument();
		var name = part.title || part.name || '';
		var _stock = _resolveStock(part);
		var inStock = _stock.status === 'in_stock';
		// Role-based feature flags — read from the shared cache.
		var __pff = (typeof window !== 'undefined' && window.__PARTS_FINDER_FEATURES__) || {};

		// Card root — mirrors the OE Commerce core product-grid card
		// (<article class="pc-card">) so it inherits the storefront's own
		// product-card CSS and renders identically to the native grid.
		var card = topDoc.createElement('article');
		card.className = 'pc-card';
		card.setAttribute('data-mh', 'product');
		card.setAttribute('data-id', part.id);
		card.setAttribute('data-name', name);
		card.setAttribute('data-url', '#');
		card.setAttribute('data-wishlist', 'True');
		card.setAttribute('data-compare', 'False');
		card.setAttribute('data-stock', inStock ? 'true' : 'false');
		card.setAttribute('data-variants', 'false');

		// Image — href="#"; on click resolve the storefront URL via API then redirect.
		var imgLink = topDoc.createElement('a');
		imgLink.className = 'pc-img-link';
		imgLink.href = '#';
		imgLink.setAttribute('tabindex', '0');
		imgLink.addEventListener('click', function (e) {
			e.preventDefault(); e.stopPropagation();
			_navigateToPartDetailUrl(part.id);
		});

		var imgWrap = topDoc.createElement('div');
		imgWrap.className = 'pc-img-wrap';
		imgWrap.style.position = 'relative';

		var productImg = topDoc.createElement('div');
		productImg.className = 'pc-img';
		productImg.setAttribute('role', 'img');
		productImg.setAttribute('aria-label', name || part.sku);
		if (part.imageUrl) {
			productImg.style.backgroundImage = 'url(\'' + part.imageUrl + '?height=262&width=262\')';
		} else {
			productImg.style.backgroundColor = '#f8f9fa';
		}
		imgWrap.appendChild(productImg);

		// Badges wrapper kept (matches core markup) but intentionally EMPTY — no
		// in-stock / out-of-stock badge is shown on the slider tiles.
		var badgesWrapper = topDoc.createElement('div');
		badgesWrapper.className = 'badges-wrapper';
		imgWrap.appendChild(badgesWrapper);

		imgLink.appendChild(imgWrap);
		card.appendChild(imgLink);

		// Body
		var body = topDoc.createElement('div');
		body.className = 'pc-body';

		// Brand — show the brand logo (search returns it as `manufacturerImage`,
		// the S3-hosted brand swatch), matching the core card. Fall back to the
		// brand name as text only when no logo is available.
		if (part.brand) {
			var brand = topDoc.createElement('div');
			brand.className = 'pc-brand';
			var brandLink = topDoc.createElement('a');
			brandLink.href = '/brand/' + encodeURIComponent(part.brand);
			brandLink.setAttribute('tabindex', '0');
			var brandImgUrl = part.manufacturerImage || part.brandImageUrl;
			if (brandImgUrl) {
				var brandImg = topDoc.createElement('img');
				brandImg.src = brandImgUrl;
				brandImg.alt = part.brand;
				brandImg.loading = 'lazy';
				brandLink.appendChild(brandImg);
			} else {
				brandLink.textContent = part.brand;
			}
			brand.appendChild(brandLink);
			body.appendChild(brand);
		}

		// Name — href="#"; click resolves the storefront URL via API.
		var nameLink = topDoc.createElement('a');
		nameLink.className = 'pc-name-link';
		nameLink.href = '#';
		nameLink.setAttribute('tabindex', '0');
		nameLink.addEventListener('click', function (e) {
			e.preventDefault(); e.stopPropagation();
			_navigateToPartDetailUrl(part.id);
		});
		var title = topDoc.createElement('h3');
		title.className = 'pc-name';
		title.textContent = name;
		nameLink.appendChild(title);
		body.appendChild(nameLink);

		// SKU
		if (part.sku) {
			var skuEl = topDoc.createElement('p');
			skuEl.className = 'pc-sku';
			skuEl.textContent = part.sku;
			body.appendChild(skuEl);
		}

		// Price — gated on `showPrices`. Formatting via the shared helper (excl. VAT).
		if (__pff.showPrices !== false) {
			var priceRow = topDoc.createElement('div');
			priceRow.className = 'pc-price-row';
			var priceSpan = topDoc.createElement('span');
			priceSpan.className = 'pc-price';
			var exclStr = formatPriceExcl(part.price);
			if (exclStr) {
				priceSpan.textContent = exclStr;
			} else {
				priceSpan.textContent = 'Price on request';
			}
			priceRow.appendChild(priceSpan);
			if (exclStr) {
				var priceTax = topDoc.createElement('span');
				priceTax.className = 'pc-price-tax';
				priceTax.textContent = 'excl. VAT';
				priceRow.appendChild(priceTax);
			}
			body.appendChild(priceRow);
		}

		// Compare
		var compare = topDoc.createElement('div');
		compare.className = 'pc-compare';
		var compareInput = topDoc.createElement('input');
		compareInput.type = 'checkbox';
		compareInput.id = 'cb_' + part.sku;
		compareInput.name = 'Compare';
		compareInput.setAttribute('data-selector', '');
		compareInput.setAttribute('data-product-sku', part.sku);
		compareInput.setAttribute('tabindex', '0');
		var compareLabel = topDoc.createElement('label');
		compareLabel.setAttribute('for', 'cb_' + part.sku);
		compareLabel.textContent = 'Add to Compare';
		compare.appendChild(compareInput);
		compare.appendChild(compareLabel);
		body.appendChild(compare);

		card.appendChild(body);

		// Actions — Details + Add to Cart, matching the core card's .pc-actions row.
		var actions = topDoc.createElement('div');
		actions.className = 'pc-actions';
		var btnRow = topDoc.createElement('div');
		btnRow.className = 'pc-btn-row';

		// Details — href="#"; resolve URL via API (OrderEazi SDK) then redirect, show loading.
		var viewBtn = topDoc.createElement('a');
		viewBtn.className = 'btn btn-outline pc-btn-details';
		viewBtn.href = '#';
		viewBtn.setAttribute('tabindex', '0');
		// Icon on mobile, "Details" text on desktop (see the media query in the
		// slider's injected CSS) — the label doesn't fit on a narrow card.
		viewBtn.innerHTML = '<span class="material-symbols-outlined pc-btn-details-icon" aria-hidden="true">read_more</span><span class="pc-btn-details-text">Details</span>';
		viewBtn.addEventListener('click', function (e) {
			e.preventDefault(); e.stopPropagation();
			_navigateToPartDetailUrl(part.id, viewBtn);
		});
		btnRow.appendChild(viewBtn);

		// Add to Cart — gated on `allowAddToCart`; adds by SKU via /ajax/cart/add-item.
		if (__pff.allowAddToCart !== false) {
			var addBtn = topDoc.createElement('a');
			addBtn.className = 'btn btn-primary pc-btn-cart quick-add';
			addBtn.href = '#';
			addBtn.setAttribute('data-product-id', part.id);
			addBtn.setAttribute('data-name', name);
			// NB: deliberately NOT setting the storefront's `add-to-cart` attribute.
			// Our own click handler (below) owns the add so it can route through
			// PFCreditGuard for B2B credit gating. If the storefront's native cart JS
			// also bound to this button it would (a) overwrite our icon-only spinner
			// with an "Adding..." label that overflows the small square button on
			// mobile, and (b) add to cart while bypassing the credit gate.
			addBtn.setAttribute('sf-ga-trigger', 'click');
			addBtn.setAttribute('sf-ga-event', 'add_to_cart');
			addBtn.setAttribute('sf-ga-data-endpoint', '/ajax/google-analytics-4/view/item?productId=' + part.id);
			addBtn.setAttribute('aria-label', 'Add ' + name + ' to cart');
			addBtn.setAttribute('tabindex', '0');
			addBtn.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">add_shopping_cart</span>';
			addBtn.addEventListener('click', function (e) {
				e.preventDefault(); e.stopPropagation();
				_addToCartWithSku(part.id, 1, addBtn);
			});
			btnRow.appendChild(addBtn);
		}

		actions.appendChild(btnRow);
		card.appendChild(actions);

		return card;
	}

	function _openQuickViewModal(partId) {
		var topDoc = getTopDocument();
		var topBody = getTopBody();

		var modalId = 'parts-finder-quick-view-modal';
		var modal = topDoc.getElementById(modalId);

		if (!modal) {
			modal = topDoc.createElement('div');
			modal.id = modalId;
			modal.className = 'parts-finder-quick-view-modal';

			var drawer = topDoc.createElement('div');
			drawer.id = 'pf-qv-drawer';

			// Header row with title + close
			var closeRow = topDoc.createElement('div');
			closeRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid #e5e7eb;flex-shrink:0;background:#fff;';
			var closeTitle = topDoc.createElement('span');
			closeTitle.style.cssText = 'font-size:15px;font-weight:700;color:#111827;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;';
			closeTitle.textContent = 'Part Details';
			var closeBtn = topDoc.createElement('button');
			closeBtn.setAttribute('aria-label', 'Close');
			closeBtn.style.cssText = 'width:32px;height:32px;border-radius:50%;border:1px solid #e5e7eb;background:#f9fafb;color:#6b7280;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:18px;line-height:1;';
			closeBtn.innerHTML = '<svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>';

			var closeDrawer = function () {
				modal.classList.remove('pf-qv-open');
				drawer.classList.remove('pf-qv-open');
				drawer.style.transform = 'translateX(100%)';
				var ca = drawer.querySelector('.pf-qv-body');
				if (ca) setTimeout(function () { ca.innerHTML = ''; }, 300);
			};

			closeBtn.addEventListener('click', closeDrawer);
			closeRow.appendChild(closeTitle);
			closeRow.appendChild(closeBtn);

			var contentArea = topDoc.createElement('div');
			contentArea.className = 'parts-finder-quick-view-body pf-qv-body';

			drawer.appendChild(closeRow);
			drawer.appendChild(contentArea);
			modal.appendChild(drawer);

			modal.addEventListener('click', function (e) {
				if (e.target === modal) closeDrawer();
			});

			topDoc.addEventListener('keydown', function (e) {
				if (e.key === 'Escape' && modal.classList.contains('pf-qv-open')) closeDrawer();
			});

			topBody.appendChild(modal);
		}

		var drawer = topDoc.getElementById('pf-qv-drawer');
		var contentArea = modal.querySelector('.parts-finder-quick-view-body');

		modal.classList.add('pf-qv-open');
		drawer.classList.add('pf-qv-open');
		drawer.style.transform = '';

		contentArea.innerHTML = '<div style="text-align:center;padding:60px 20px;"><div style="display:inline-block;width:48px;height:48px;border:4px solid #f3f3f3;border-top:4px solid #d9272e;border-radius:50%;animation:pf-spin .8s linear infinite;"></div><p style="margin-top:20px;color:#6c757d;font-size:15px;">Loading part details...</p></div>';

		modal._pendingPartId = partId;
		modal._pendingContentArea = contentArea;

		_sendMessageToIframe({ type: 'GET_PART_DETAIL', data: { partId: partId } });
	}

	function _renderPartDetailsInModal(container, part, partId) {
		// Get images
		var images = (part.imageUrls && part.imageUrls.length > 0) ? part.imageUrls : (part.imageUrl ? [part.imageUrl] : []);

		// Stock status styling (matching React PartDetailModal)
		var _modalStock = _resolveStock(part);
		var stockStatus = _modalStock.status;
		var stockBadgeClass = stockStatus === 'in_stock' ? 'background: #d1fae5; color: #065f46;' :
		                      stockStatus === 'backorder' ? 'background: #fef3c7; color: #92400e;' :
		                      'background: #fee2e2; color: #991b1b;';
		// showStockQuantity gates the numeric count; off → status-only.
		var _modalShowQ = ((window.__PARTS_FINDER_FEATURES__ || {}).showStockQuantity) !== false;
		var stockBadgeText = (_modalShowQ && _modalStock.qty !== undefined && _modalStock.qty > 0)
			? (_modalStock.qty + ' In Stock')
			: (stockStatus === 'in_stock' ? 'In Stock' : stockStatus === 'backorder' ? 'Backorder' : 'Out Of Stock');

		// Price via the shared helper (incl. VAT = excl * 1.15).
		var priceInclStr = formatPriceIncl(part.price);
		var priceExclStr = formatPriceExcl(part.price);

		// Single column stacked layout for side drawer
		var s = 'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;';
		var html = '<div style="display:flex;flex-direction:column;padding:20px;gap:20px;' + s + '">';

		// Image
		html += '<div style="background:#f9fafb;border-radius:8px;padding:16px;display:flex;align-items:center;justify-content:center;min-height:200px;">';
		if (images.length > 0) {
			html += '<img id="pf-qv-main-img" src="' + images[0] + '" alt="' + (part.title || 'Part') + '" style="max-width:100%;max-height:240px;object-fit:contain;" onerror="this.style.display=\'none\'" />';
		} else {
			html += '<div style="color:#d1d5db;font-size:14px;">No image available</div>';
		}
		html += '</div>';

		// Thumbnails
		if (images.length > 1) {
			html += '<div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;">';
			for (var i = 0; i < Math.min(images.length, 6); i++) {
				var borderStyle = i === 0 ? 'border:2px solid #d9272e;' : 'border:1px solid #e5e7eb;';
				html += '<button data-src="' + images[i] + '" class="pf-qv-thumb" style="width:48px;height:48px;border-radius:6px;overflow:hidden;cursor:pointer;' + borderStyle + 'padding:0;background:none;">';
				html += '<img src="' + images[i] + '" alt="" style="width:100%;height:100%;object-fit:cover;" />';
				html += '</button>';
			}
			html += '</div>';
		}

		// Title
		html += '<h2 style="font-size:17px;font-weight:700;color:#111827;margin:0;line-height:1.4;">' + (part.title || 'Part Details') + '</h2>';

		// SKU / Brand / Category
		html += '<div style="display:flex;flex-direction:column;gap:6px;">';
		html += '<div style="display:flex;align-items:center;gap:8px;"><span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;min-width:64px;">SKU</span><span style="font-size:13px;font-weight:700;font-family:monospace;color:#111827;">' + (part.sku || 'N/A') + '</span></div>';
		if (part.brand) html += '<div style="display:flex;align-items:center;gap:8px;"><span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;min-width:64px;">Brand</span><span style="font-size:13px;font-weight:600;color:#111827;">' + part.brand + '</span></div>';
		if (part.category) html += '<div style="display:flex;align-items:center;gap:8px;"><span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;min-width:64px;">Category</span><span style="font-size:13px;font-weight:600;color:#111827;">' + part.category + '</span></div>';
		html += '</div>';

		// Price + stock — each piece gated by feature flag.
		var __qvpff = (typeof window !== 'undefined' && window.__PARTS_FINDER_FEATURES__) || {};
		if (__qvpff.showPrices !== false || __qvpff.showStockStatus !== false) {
			html += '<div style="background:#f9fafb;border-radius:8px;padding:16px;border:1px solid #e5e7eb;">';
			if (__qvpff.showPrices !== false) {
				if (priceInclStr) {
					if (__qvpff.showPriceInclVat !== false) {
						html += '<div style="font-size:26px;font-weight:800;color:#2c347c;line-height:1.2;">' + priceInclStr + '<span style="font-size:12px;font-weight:400;color:#6b7280;margin-left:4px;">incl. VAT</span></div>';
					}
					if (__qvpff.showPriceExclVat !== false) {
						html += '<div style="font-size:12px;color:#6b7280;margin-top:2px;">' + priceExclStr + ' excl. VAT</div>';
					}
				} else {
					html += '<div style="font-size:16px;font-weight:600;color:#4b5563;">Price on request</div>';
				}
			}
			if (__qvpff.showStockStatus !== false) {
				html += '<span style="display:inline-block;margin-top:8px;padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:700;' + stockBadgeClass + '">' + stockBadgeText + '</span>';
			}
			html += '</div>';
		}

		// OEM Codes — gated on `showOemCodes`.
		if (__qvpff.showOemCodes !== false && part.oem_codes && part.oem_codes.length > 0) {
			html += '<div><h3 style="font-size:13px;font-weight:700;color:#111827;margin:0 0 8px;">OEM Codes</h3>';
			html += '<div style="display:flex;flex-direction:column;gap:4px;">';
			for (var i = 0; i < part.oem_codes.length; i++) {
				var oemRaw = part.oem_codes[i];
				var oemColon = oemRaw.indexOf(':');
				var oemMfr = oemColon > -1 ? oemRaw.substring(0, oemColon).trim() : '';
				var oemVal = oemColon > -1 ? oemRaw.substring(oemColon + 1).trim() : oemRaw;
				if (oemMfr) {
					html += '<div style="display:flex;align-items:center;gap:8px;padding:4px 10px;background:#f3f4f6;border-radius:6px;">';
					html += '<span style="font-size:11px;font-weight:700;color:#6b7280;min-width:80px;">' + oemMfr + '</span>';
					html += '<span style="font-size:12px;font-weight:600;color:#1f2937;font-family:monospace;">' + oemVal + '</span>';
					html += '</div>';
				} else {
					html += '<span style="display:inline-block;background:#f3f4f6;color:#1f2937;font-size:12px;font-weight:500;padding:3px 10px;border-radius:6px;font-family:monospace;">' + oemVal + '</span>';
				}
			}
			html += '</div></div>';
		}

		// Attributes
		if (part.attributes && part.attributes.length > 0) {
			html += '<div><h3 style="font-size:13px;font-weight:700;color:#111827;margin:0 0 8px;">Attributes</h3><div style="display:flex;flex-direction:column;gap:6px;">';
			for (var i = 0; i < part.attributes.length; i++) {
				var attr = part.attributes[i];
				var colonIdx = attr.indexOf(':');
				var key = colonIdx > -1 ? attr.substring(0, colonIdx) : attr;
				var value = colonIdx > -1 ? attr.substring(colonIdx + 1).trim() : '';
				html += '<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:8px 12px;background:#f9fafb;border-radius:6px;font-size:13px;border:1px solid #f3f4f6;"><span style="font-weight:600;color:#6b7280;">' + key + '</span><span style="font-weight:700;color:#111827;text-align:right;word-break:break-word;">' + value + '</span></div>';
			}
			html += '</div></div>';
		}

		// Vehicle Compatibility — gated on `showCompatibility`.
		if (__qvpff.showCompatibility !== false && part.fitments && part.fitments.length > 0) {
			html += '<div><div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;"><h3 style="font-size:13px;font-weight:700;color:#111827;margin:0;">Vehicle Compatibility</h3><span style="font-size:12px;color:#6b7280;">' + part.fitments.length + ' vehicle' + (part.fitments.length !== 1 ? 's' : '') + '</span></div>';
			html += '<div style="display:flex;flex-direction:column;gap:6px;">';
			for (var i = 0; i < part.fitments.length; i++) {
				var fit = part.fitments[i];
				var yearRange = '';
				if (fit.fit_year_from && fit.fit_year_to) {
					yearRange = fit.fit_year_from === fit.fit_year_to ? fit.fit_year_from : fit.fit_year_from + ' - ' + fit.fit_year_to;
				} else if (fit.fit_year_from) {
					yearRange = fit.fit_year_from + '+';
				} else if (fit.fit_year_to) {
					yearRange = 'Up to ' + fit.fit_year_to;
				}
				html += '<div style="display:flex;flex-direction:column;padding:8px 12px;border-radius:6px;background:#f9fafb;font-size:12px;">';
				html += '<div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:4px;">';
				if (fit.fit_make) html += '<span style="background:#2c347c;color:#fff;font-weight:700;padding:2px 8px;border-radius:4px;">' + fit.fit_make + '</span>';
				if (fit.fit_model) html += '<span style="background:#374151;color:#fff;font-weight:600;padding:2px 8px;border-radius:4px;">' + fit.fit_model + '</span>';
				if (fit.fit_submodel) html += '<span style="background:#6b7280;color:#fff;font-weight:500;padding:2px 8px;border-radius:4px;">' + fit.fit_submodel + '</span>';
				html += '</div><div style="display:flex;gap:12px;flex-wrap:wrap;">';
				if (yearRange) html += '<span style="color:#4b5563;font-weight:500;">' + yearRange + '</span>';
				if (fit.fit_engine) html += '<span style="color:#6b7280;">' + fit.fit_engine + '</span>';
				if (fit.fit_notes) html += '<span style="color:#9ca3af;font-style:italic;width:100%;margin-top:2px;">' + fit.fit_notes + '</span>';
				html += '</div></div>';
			}
			html += '</div></div>';
		}

		// Action buttons — Add to Cart gated on `allowAddToCart`.
		html += '<div style="display:flex;gap:12px;padding-top:16px;border-top:1px solid #e5e7eb;">';
		html += '<a href="#" class="parts-slider-view-full-details" data-part-id="' + partId.replace(/"/g, '&quot;') + '" role="button" style="flex:1;padding:10px 16px;background:#fff;border:1px solid #d9272e;color:#d9272e;text-align:center;text-decoration:none;border-radius:6px;font-size:14px;font-weight:600;" onmouseover="this.style.background=\'#d9272e\';this.style.color=\'#fff\';" onmouseout="this.style.background=\'#fff\';this.style.color=\'#d9272e\';">View Full Details</a>';
		if (__qvpff.allowAddToCart !== false) {
			// No `add-to-cart` attribute — the .quick-add click handler below owns the
			// add (routes through PFCreditGuard + shows a spinner), same as the card button.
			html += '<a href="#" data-product-id="' + partId + '" data-name="' + (part.title || '').replace(/'/g, '&#39;') + '" class="quick-add" style="flex:1;padding:10px 16px;background:#d9272e;color:#fff;text-align:center;text-decoration:none;border-radius:6px;font-size:14px;font-weight:600;" onmouseover="this.style.background=\'#b91f24\';" onmouseout="this.style.background=\'#d9272e\';">Add to Cart</a>';
		}
		html += '</div>';

		html += '</div>'; // End column

		container.innerHTML = html;

		// Wire thumbnail clicks
		container.querySelectorAll('.pf-qv-thumb').forEach(function (btn) {
			btn.addEventListener('click', function () {
				var src = btn.getAttribute('data-src');
				var mainImg = container.querySelector('#pf-qv-main-img');
				if (mainImg) mainImg.src = src;
				container.querySelectorAll('.pf-qv-thumb').forEach(function (b) {
					b.style.border = b === btn ? '2px solid #d9272e' : '1px solid #e5e7eb';
				});
			});
		});

		// Attach View Full Details - resolve URL via API (OrderEazi SDK) then redirect, show loading
		var viewFullDetailsLink = container.querySelector('.parts-slider-view-full-details');
		if (viewFullDetailsLink) {
			viewFullDetailsLink.addEventListener('click', function(e) {
				e.preventDefault();
				var pid = viewFullDetailsLink.getAttribute('data-part-id') || partId;
				_navigateToPartDetailUrl(pid, viewFullDetailsLink);
			});
		}

		// Attach add to cart: POST to /ajax/cart/add-item with { Sku, Qty }
		var addToCartBtn = container.querySelector('.quick-add');
		if (addToCartBtn) {
			addToCartBtn.addEventListener('click', function(e) {
				e.preventDefault();
				_addToCartWithSku(partId, 1, addToCartBtn);
			});
		}
	}

	function _sendMessageToIframe(message) {
		if (_iframe && _iframe.contentWindow) {
			// Use wildcard origin to allow cross-origin communication
			// The iframe script will verify the origin on its side
			_iframe.contentWindow.postMessage(message, '*');
		}
	}

	function _handleMessageFromIframe(event) {
		// Verify message origin - allow messages from parts-finder app domain
		var expectedOrigins = [];
		if (_appBaseUrl) {
			try {
				var appUrl = new URL(_appBaseUrl);
				expectedOrigins.push(appUrl.origin);
			} catch (e) {
				// Ignore URL parsing errors
			}
		}
		// Also allow from common parts-finder domains
		if (event.origin.includes('parts-finder') || event.origin.includes('storefront8.co.za')) {
			// Allow this origin
		} else if (expectedOrigins.length > 0 && expectedOrigins.indexOf(event.origin) === -1) {
			// Origin doesn't match expected origins, but allow if it's from a parts-finder domain
			if (!event.origin.includes('parts-finder') && !event.origin.includes('storefront8.co.za')) {
				return;
			}
		}

		var message = event.data;

		if (!message || !message.type) {
			return;
		}

		switch (message.type) {
			case 'IFRAME_READY':
				// Request parts when OUR iframe is ready. Other embeds' iframes announce
				// themselves too, and each of those used to trigger another request.
				if (_iframe && event.source && event.source !== _iframe.contentWindow) break;
				_requestParts();
				break;
			case 'SLIDER_PARTS_RESPONSE':
				// On a multi-slider page every embed hears every response; only
				// handle the one tagged for THIS embed's slider id. (Older responses
				// without a sliderId are handled for backward-compat.)
				if (message.data && message.data.sliderId && message.data.sliderId !== _sliderId) break;
				// A request can go out twice (the ready message and the reuse timer both
				// ask); render the first answer only, so the slider isn't rebuilt under
				// the shopper.
				if (_responded) break;
				if (message.data && message.data.results) {
					_responded = true;
					// Resolve role-based customer prices before rendering (fails open
					// to catalogue prices); render regardless of the outcome.
					var _results = message.data.results;
					_applyCustomerPrices(_results)
						.then(function () { _renderParts(_results); })
						.catch(function () { _renderParts(_results); });
				} else {
					console.warn('PartsSlider: No results in response:', message.data);
				}
				break;
			case 'SLIDER_PARTS_ERROR':
				if (message.data && message.data.sliderId && message.data.sliderId !== _sliderId) break;
				// A failed duplicate must not replace products that already rendered.
				if (_responded) break;
				console.error('PartsSlider: Error getting parts:', message.data && message.data.error);
				_removeExternalSkeleton();
				var slidesContainer = getTopDocument().getElementById(_sliderId + '-slides');
				if (slidesContainer) {
					slidesContainer.innerHTML = '<div style="padding: 40px; text-align: center; color: #dc3545;">Error loading products: ' + ((message.data && message.data.error) || 'Unknown error') + '</div>';
				}
				break;
			case 'QUICK_VIEW_REQUEST':
				// Iframe is requesting to open quick view - open modal directly in parent page
				if (message.data && message.data.partId) {
					_openQuickViewModal(message.data.partId);
				}
				break;
			case 'PART_DETAIL_RESPONSE':
				// Iframe has fetched part detail data - render in modal
				if (message.data && message.data.part && message.data.partId) {
					var modal = getTopDocument().getElementById('parts-finder-quick-view-modal');
					if (modal && modal._pendingContentArea && modal._pendingPartId === message.data.partId) {
						_renderPartDetailsInModal(modal._pendingContentArea, message.data.part, message.data.partId);
						// Clear pending references
						delete modal._pendingPartId;
						delete modal._pendingContentArea;
					}
				}
				break;
			case 'PART_DETAIL_ERROR':
				// Error fetching part detail - show error in modal
				if (message.data && message.data.partId) {
					var errModal = getTopDocument().getElementById('parts-finder-quick-view-modal');
					if (errModal && errModal._pendingContentArea && errModal._pendingPartId === message.data.partId) {
						var errorMsg = message.data.error || 'Unknown error';
						errModal._pendingContentArea.innerHTML = '<div style="text-align: center; padding: 60px 20px;"><p style="color: #dc3545; font-size: 16px; margin-bottom: 10px;">Error loading part details</p><p style="color: #6c757d; font-size: 14px;">' + errorMsg + '</p></div>';
						// Clear pending references
						delete errModal._pendingPartId;
						delete errModal._pendingContentArea;
					}
				}
				break;
			default:
				// Ignore other message types (from other scripts)
				break;
		}
	}

	// Build the slider DOM inside this instance's container and kick off the parts
	// request. Mirrors the legacy Render.InitializeSlider.
	function _initializeSlider() {
		_sliderId = 'parts-slider-' + Math.random().toString(36).substr(2, 9);
		var topDoc = getTopDocument();

		// Create hidden iframe for communication
		_renderSliderIframe();

		// Create Bootstrap container/row/col structure
		var containerDiv = topDoc.createElement('div');
		containerDiv.className = 'container';
		containerDiv.style.cssText = 'margin-top:60px;';

		var rowDiv = topDoc.createElement('div');
		rowDiv.className = 'row';

		var colDiv = topDoc.createElement('div');
		colDiv.className = 'col-md-12';

		// Create wrapper structure matching Storefront HTML
		var wrapper = topDoc.createElement('div');
		wrapper.className = 'product-grid-wrapper';
		wrapper.id = _sliderId;
		wrapper.setAttribute('data-parts-slider', 'true');

		// Create placeholder content
		var placeholder = topDoc.createElement('div');
		placeholder.className = 'sf-placeholder-content false ' + _sliderId + ' loaded';

		// Create product grid container with all data attributes
		var productGrid = topDoc.createElement('div');
		productGrid.className = 'product-grid';
		productGrid.setAttribute('data-mode', 'slider');
		productGrid.setAttribute('data-limit', _config.max || '21');
		productGrid.setAttribute('data-source', 'filter');
		productGrid.setAttribute('data-xs', '6');
		productGrid.setAttribute('data-md', _config.visible || '3');
		productGrid.setAttribute('data-dots', _config.dots !== false ? 'true' : 'false');
		productGrid.setAttribute('data-arrow', _config.arrows !== false ? 'true' : 'false');
		productGrid.setAttribute('data-infinite', _config.infinite !== false ? 'true' : 'false');
		productGrid.setAttribute('data-speed', _config.speed || '400');
		productGrid.setAttribute('data-autoplay', _config.autoplay ? 'true' : 'false');
		productGrid.setAttribute('data-columns', _config.visible || '4');
		productGrid.setAttribute('data-tablet-columns', _config.tabletColumns || '2');
		productGrid.setAttribute('data-mobile-columns', _config.mobileColumns || '1');
		productGrid.setAttribute('data-draggable', _config.draggable !== false ? 'true' : 'false');
		productGrid.setAttribute('data-use-placeholder', 'false');
		productGrid.setAttribute('data-placeholder-height', '340');
		productGrid.setAttribute('data-placeholder-color', '#f1f5f7');
		productGrid.id = _sliderId;

		// Create previous button (before slides)
		var prevBtn = topDoc.createElement('button');
		prevBtn.type = 'button';
		prevBtn.setAttribute('data-role', 'none');
		prevBtn.className = 'slick-prev slick-arrow slider-prev';
		prevBtn.setAttribute('aria-label', 'Previous');
		prevBtn.setAttribute('role', 'button');
		prevBtn.innerHTML = '<span><i class="fa fa-angle-left"></i></span>';

		// Create slides container (will be initialized by Slick)
		var slidesContainer = topDoc.createElement('div');
		slidesContainer.className = 'slides-contain';
		slidesContainer.id = _sliderId + '-slides';

		// Loading state = skeleton cards. It lives inside the slides container,
		// so _renderParts()'s `slidesContainer.innerHTML = ''` clears it the moment
		// the real parts arrive (no separate teardown needed).
		var loading = topDoc.createElement('div');
		loading.className = 'parts-slider-loading';
		loading.innerHTML = _skeletonHtml(parseInt(_config.visible || 5, 10) || 5);
		slidesContainer.appendChild(loading);

		// Create next button (after slides)
		var nextBtn = topDoc.createElement('button');
		nextBtn.type = 'button';
		nextBtn.setAttribute('data-role', 'none');
		nextBtn.className = 'slick-next slick-arrow slider-next';
		nextBtn.setAttribute('aria-label', 'Next');
		nextBtn.setAttribute('role', 'button');
		nextBtn.innerHTML = '<span><i class="fa fa-angle-right"></i></span>';

		// Create arrows container (hidden by default)
		var arrowsContainer = topDoc.createElement('div');
		arrowsContainer.className = 'slick-arrows-contain hide';
		var arrowsPrev = prevBtn.cloneNode(true);
		var arrowsNext = nextBtn.cloneNode(true);
		arrowsContainer.appendChild(arrowsPrev);
		arrowsContainer.appendChild(arrowsNext);

		// Assemble structure: product-grid > buttons and slides
		productGrid.appendChild(prevBtn);
		productGrid.appendChild(slidesContainer);
		productGrid.appendChild(nextBtn);
		productGrid.appendChild(arrowsContainer);
		placeholder.appendChild(productGrid);
		wrapper.appendChild(placeholder);

		// Bootstrap structure: container > row > col-md-12 > wrapper
		colDiv.appendChild(wrapper);
		rowDiv.appendChild(colDiv);
		containerDiv.appendChild(rowDiv);

		// The container is resolved from the top-level document in init(), so it is
		// already in the top-level page — append the built structure straight into it.
		_container = container;
		_container.appendChild(containerDiv);

		// Request parts data
		_requestParts();
	}

	// Public entry for this instance: parse config off the container, wire the
	// postMessage listener, then build + populate.
	function start() {
		// Per-instance config — every old <script data-*> now lives on the container.
		var ds = container.dataset || {};
		_config = {
			max: ds.max || '21',
			// Defaults mirror the OE Commerce core product-grid slider (5 desktop /
			// 2 tablet / 2 mobile, 1000ms) so tile size matches the native grid.
			visible: ds.visible || '5',
			department: ds.department || '',
			make: ds.make || '',
			model: ds.model || '',
			year: ds.year || '',
			engine: ds.engine || '',
			category: ds.category || '',
			brand: ds.brand || '',
			dots: ds.dots !== 'false',
			arrows: ds.arrows !== 'false',
			infinite: ds.infinite !== 'false',
			speed: ds.speed || '1000',
			autoplay: ds.autoplay === 'true',
			tabletColumns: ds.tabletColumns || '2',
			mobileColumns: ds.mobileColumns || '2',
			draggable: ds.draggable !== 'false',
			skeleton: ds.skeleton || ''
		};

		// Set up postMessage listener (one per instance, filtering by sliderId).
		window.addEventListener('message', _handleMessageFromIframe);

		// Small settle delay before building (preserves the legacy timing).
		setTimeout(_initializeSlider, 100);
	}

	return { start: start };
}

export function init() {
	// Snapshot the containers up front: the slider marks its own internal wrapper
	// and comm iframe with data-parts-slider too, so we must not re-query live.
	var containers = getTopDocument().querySelectorAll(CONTAINER_SELECTOR);
	if (!containers || !containers.length) return; // not on a page with this widget

	// Populate the shared role-feature cache once (window.__PARTS_FINDER_FEATURES__)
	// so tile builders read the real flags. Fire-and-forget: builders fall back to
	// all-true until it resolves. Replaces the legacy inline /api/features fetch.
	try { loadFeatures(); } catch (e) { /* ignore */ }

	var list = Array.prototype.slice.call(containers);
	for (var i = 0; i < list.length; i++) {
		var el = list[i];
		// Guard against double-init (and against matching our own injected markup).
		if (el.getAttribute('data-pf-slider-init') === 'true') continue;
		el.setAttribute('data-pf-slider-init', 'true');
		try {
			createSlider(el).start();
		} catch (e) {
			try { console.warn('PartsSlider: instance init failed', e); } catch (_) { /* ignore */ }
		}
	}
}

export var id = 'parts-slider';
